import { LEVELS, rowIdentity } from "./variantText";

// The variant's own fields a change list names, with their labels.
const FIELDS = [
  ["name", "variants.fields.name"],
  ["heading", "variants.fields.heading"],
  ["description", "variants.fields.description"],
  ["calculator_is_allowed", "test_bank.form.calculator_allowed"],
  ["periodic_table_is_allowed", "test_bank.form.periodic_table_allowed"],
  ["additional_information", "test_bank.form.additional_information"],
  ["keep_order", "variants.keep.title"],
];
const MAX_LINES = 8;

const sameValue = (key, a, b) => {
  if (key === "keep_order") return JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());
  if (typeof a === "boolean" || typeof b === "boolean") return !!a === !!b;
  return (a ?? "") === (b ?? "");
};

/**
 * What someone else changed in a variant between two reads of it (`before` and `after`, details), in plain words,
 * and the places of the composition it touched: {lines, more (lines over the first few), tqs (rows whose question
 * came or changed), emptied ("rowIdentity:level" places a question left: by the matrix row's topic and subtopics,
 * which a save of the matrix between the two reads keeps, unlike its uuid)}. A row keeps its uuid when its question
 * is replaced and the numbers after a removed question move, so rows are matched by uuid, never by number.
 */
export function variantChanges(t, before, after) {
  const lines = [];
  const tqs = new Set();
  const emptied = new Set();
  const oldRows = new Map((before?.test_questions || []).map((tq) => [tq.uuid, tq]));
  const newRows = new Map((after?.test_questions || []).map((tq) => [tq.uuid, tq]));
  const code = (tq) => tq?.question?.code || "—";

  if (oldRows.size && newRows.size && ![...oldRows.keys()].some((uuid) => newRows.has(uuid))) {
    // Proposed anew: every row is new.
    lines.push(t("variants.changed.reproposed"));
    newRows.forEach((_, uuid) => tqs.add(uuid));
  } else {
    newRows.forEach((tq, uuid) => {
      const old = oldRows.get(uuid);
      if (!old) {
        lines.push(t("variants.changed.added", { number: tq.order, code: code(tq) }));
        tqs.add(uuid);
      } else if (old.question?.uuid !== tq.question?.uuid) {
        lines.push(t("variants.changed.replaced", { number: tq.order, from: code(old), to: code(tq) }));
        tqs.add(uuid);
      }
    });
    // Where each removed row stood in the matrix, by the check as it was.
    const placeOf = new Map();
    (before?.check?.rows || []).forEach((entry) =>
      LEVELS.forEach((level) => (entry.test_questions?.[level] || []).forEach((uuid) => placeOf.set(uuid, `${rowIdentity(entry.row)}:${level}`)))
    );
    oldRows.forEach((tq, uuid) => {
      if (newRows.has(uuid)) return;
      lines.push(t("variants.changed.removed", { number: tq.order, code: code(tq) }));
      if (placeOf.has(uuid)) emptied.add(placeOf.get(uuid));
    });
  }
  if (before?.status !== after?.status) {
    lines.push(t(after?.status === "confirmed" ? "variants.changed.confirmed" : "variants.changed.unconfirmed"));
  }
  const fields = FIELDS.filter(([key]) => !sameValue(key, before?.[key], after?.[key])).map(([, label]) => t(label));
  if (fields.length) lines.push(t("variants.changed.fields", { fields: fields.join(", ") }));

  return { lines: lines.slice(0, MAX_LINES), more: Math.max(0, lines.length - MAX_LINES), tqs, emptied };
}
